import React, { useState, useEffect } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, ArrowRight, Image as ImageIcon } from "lucide-react";
import { services } from "../data";
import ContactForm from "../components/ContactForm";

export default function ServiceDetail() {
  const { id } = useParams();
  const service = services.find((s) => s.id === id);

  const [galleryItems, setGalleryItems] = useState([]);
  const [loadingGallery, setLoadingGallery] = useState(true);
  const [activeLightboxImage, setActiveLightboxImage] = useState(null);

  useEffect(() => {
    if (!service) return;

    async function fetchGallery() {
      setLoadingGallery(true);
      try {
        // Hent galleri-billeder tagget med denne ydelses kategori-slug via v2-endpointet
        const res = await fetch(`https://api.dev.auxo.dk/public/v2/content/gallery?term=byggeri-category:${service.id}`);
        if (res.ok) {
          const data = await res.json();
          setGalleryItems(data.items || []);
        }
      } catch (err) {
        console.error("Fejl ved hentning af galleri:", err);
      } finally {
        setLoadingGallery(false);
      }
    }

    fetchGallery();
  }, [service, id]);

  // Hvis ydelsen ikke findes, omdirigerer vi til 404
  if (!service) {
    return <Navigate to="/404" replace />;
  }

  const serviceImageResized = `https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/${service.image.split("/").pop()}`;

  return (
    <>
      <Helmet>
        <title>{service.seo.title}</title>
        <meta name="description" content={service.seo.description} />
        <meta property="og:title" content={service.seo.title} />
        <meta property="og:description" content={service.seo.description} />
        <meta property="og:image" content={service.image} />
        <link rel="canonical" href={`https://hennebergbyggeri.dk/ydelser/${service.id}`} />
      </Helmet>

      {/* Hovedindhold */}
      <section className="py-12 sm:py-16 bg-white text-neutral-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-neutral-900 hover:text-neutral-600 hover:underline mb-4"
            >
              ← Forside
            </Link>
            <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl text-neutral-900 uppercase">
              {service.title}
            </h1>
            <p className="mt-3 text-base sm:text-lg text-gray-500 max-w-3xl">
              {service.shortDescription}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Beskrivelse & Fordele */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-neutral-900 uppercase">
                  Professionel løsning og rådgivning
                </h2>
                <p className="mt-4 text-base text-gray-600 leading-relaxed">
                  {service.longDescription}
                </p>
              </div>

              <div className="border-t border-gray-100 pt-8">
                <h3 className="text-lg font-bold text-neutral-900 mb-4">
                  Dine fordele ved denne løsning:
                </h3>
                <ul className="space-y-3.5">
                  {service.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="flex h-5 w-5 shrink-0 items-center justify-center text-neutral-900 mt-0.5">
                        <CheckCircle2 className="h-4 w-4" />
                      </div>
                      <span className="text-sm text-gray-600 leading-relaxed">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Procesforløb */}
            <div className="rounded-lg border border-gray-100 bg-neutral-50 p-6 sm:p-8">
              <h3 className="text-lg font-bold text-neutral-900 mb-6">
                Sådan forløber processen:
              </h3>
              <div className="relative border-l-2 border-gray-200 pl-6 ml-2 space-y-8">
                {service.process.map((step, index) => (
                  <div key={index} className="relative">
                    {/* Punkt på tidslinje */}
                    <div className="absolute -left-[33px] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border-2 border-[#C2A679] bg-white"></div>
                    <h4 className="text-sm font-bold text-neutral-900">
                      Trin {index + 1}: {step.title}
                    </h4>
                    <p className="mt-1.5 text-xs sm:text-sm text-gray-600 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projektgalleri via Galleri CMS-reference */}
      <section className="py-16 sm:py-24 bg-neutral-50 border-t border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-neutral-900 sm:text-3xl uppercase">
              Projektgalleri for {service.title}
            </h2>
            <p className="mt-3 text-sm text-gray-600">
              Se et udvalg af vores færdiggjorte projekter og lad dig inspirere til dit eget byggeri.
            </p>
          </div>

          {loadingGallery ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#C2A679] border-t-transparent"></div>
            </div>
          ) : (() => {
            const flattenedImages = [];
            galleryItems.forEach((item) => {
              if (Array.isArray(item.image)) {
                item.image.forEach((imgUrl, imgIndex) => {
                  flattenedImages.push({
                    id: `${item.id}-${imgIndex}`,
                    image: imgUrl,
                    title: item.title?.da || item.title,
                  });
                });
              } else if (item.image) {
                flattenedImages.push({
                  id: `${item.id}-0`,
                  image: item.image,
                  title: item.title?.da || item.title,
                });
              }
            });

            if (flattenedImages.length === 0) {
              return (
                <div className="text-center py-12 rounded-lg border border-dashed border-gray-300 bg-white">
                  <ImageIcon className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                  <p className="text-sm text-gray-500">Der er i øjeblikket ingen billeder i dette galleri.</p>
                </div>
              );
            }

            return (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {flattenedImages.map((imgItem) => {
                  const imageFilename = imgItem.image?.split("/").pop();
                  const galleryImageResized = `https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/${imageFilename}`;

                  return (
                    <div
                      key={imgItem.id}
                      onClick={() => setActiveLightboxImage(imgItem.image)}
                      className="group relative overflow-hidden bg-gray-100 shadow-sm cursor-pointer aspect-[4/3] border border-gray-200"
                    >
                      <img
                        src={galleryImageResized}
                        alt={imgItem.title}
                        className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Kontakt CTA Sektion */}
      <section className="py-10 sm:py-14 bg-white text-neutral-900 border-t border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-center">
            <div>
              <h2 className="mt-2 text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl uppercase">
                Skal vi hjælpe dig med {service.title.toLowerCase()}?
              </h2>
              <p className="mt-4 text-base text-gray-600 leading-relaxed">
                Uanset om du har brug for rådgivning, et uforpligtende prisoverslag eller et færdigt tilbud på {service.title.toLowerCase()}, står vi klar til at hjælpe dig videre. Kontakt os i dag, og lad os realisere dit projekt sammen.
              </p>
            </div>
            <div>
              <ContactForm projectType={service.title} />
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox Visning */}
      {activeLightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setActiveLightboxImage(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-[#C2A679] text-2xl font-bold focus:outline-none"
            onClick={() => setActiveLightboxImage(null)}
          >
            ✕
          </button>
          <img
            src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/531e2906-0b4f-4a2d-bfb4-6b0bf7bd2bc6/${activeLightboxImage.split("/").pop()}`}
            alt="Forstørret referencebillede"
            className="max-h-[90vh] max-w-full object-contain"
          />
        </div>
      )}
    </>
  );
}
